import { msg } from '@lingui/core/macro';

import type { BatMetadataConfigEntry } from './common/types';
import { coreFields } from './common/commonFields';
import { locationFields } from './common/locationFields';

export const employeesMetadata = {
  displayName: msg({
    message: 'Employees',
    context: 'Activity data schema name for employees',
  }),
  fields: {
    ...coreFields,
    ...locationFields,
    commute_bike_distance: {
      displayName: msg({
        message: 'Average distance commuted by bike',
        context:
          'The average distance commuted by bike per employee-day. (Typically from commute survey data averaged by office location.)',
      }),
      externalDescription: msg({
        message:
          'The average distance commuted by bike per employee-day. (Typically from commute survey data averaged by office location.)',
        context: 'Average distance commuted by bike',
      }),
    },
    commute_bike_percent: {
      displayName: msg({
        message: 'Percent commute by bike',
        context:
          'The percentage (between 0 and 100) of employee-days spent commuting on-site by bike. (Typically from commute survey data averaged by office location.)',
      }),
      externalDescription: msg({
        message:
          'The percentage (between 0 and 100) of employee-days spent commuting on-site by bike. (Typically from commute survey data averaged by office location.)',
        context: 'Percent commute by bike',
      }),
    },
    commute_car_distance: {
      displayName: msg({
        message: 'Average distance commuted by car',
        context:
          'The average distance commuted by car per employee-day. (Typically from commute survey data averaged by office location.)',
      }),
      externalDescription: msg({
        message:
          'The average distance commuted by car per employee-day. (Typically from commute survey data averaged by office location.)',
        context: 'Average distance commuted by car',
      }),
    },
    commute_car_percent: {
      displayName: msg({
        message: 'Percent commute by car',
        context:
          'The percentage (between 0 and 100) of employee-days spent commuting on-site by car. (Typically from commute survey data averaged by office location.)',
      }),
      externalDescription: msg({
        message:
          'The percentage (between 0 and 100) of employee-days spent commuting on-site by car. (Typically from commute survey data averaged by office location.)',
        context: 'Percent commute by car',
      }),
    },
    commute_distance_unit: {
      displayName: msg({
        message: 'Commute distance unit',
        context:
          'Label for a group of units of employee commute. Values might be km, miles, etc.',
      }),
      externalDescription: msg({
        message: 'The unit of distance for employee commute info.',
        context: 'Label describing a unit of distance, e.g. km or miles.',
      }),
    },
    commute_transit_distance: {
      displayName: msg({
        message: 'Average distance commuted by transit',
        context:
          'The average distance commuted by public transit per employee-day. (Typically from commute survey data averaged by office location.)',
      }),
      externalDescription: msg({
        message:
          'The average distance commuted by public transit per employee-day. (Typically from commute survey data averaged by office location.)',
        context: 'Average distance commuted by transit',
      }),
    },
    commute_transit_percent: {
      displayName: msg({
        message: 'Percent commute by transit',
        context:
          'The percentage (between 0 and 100) of employee-days spent commuting on-site by public transit. (Typically from commute survey data averaged by office location.)',
      }),
      externalDescription: msg({
        message:
          'The percentage (between 0 and 100) of employee-days spent commuting on-site by public transit. (Typically from commute survey data averaged by office location.)',
        context: 'Percent commute by transit',
      }),
    },
    commute_walk_distance: {
      displayName: msg({
        message: 'Average distance commuted by walk',
        context:
          'The average distance commuted by walk per employee-day. (Typically from commute survey data averaged by office location.)',
      }),
      externalDescription: msg({
        message:
          'The average distance commuted by walk per employee-day. (Typically from commute survey data averaged by office location.)',
        context: 'Average distance commuted by walk',
      }),
    },
    commute_walk_percent: {
      displayName: msg({
        message: 'Percent commute by walk',
        context:
          'The percentage (between 0 and 100) of employee-days spent commuting on-site by walk. (Typically from commute survey data averaged by office location.)',
      }),
      externalDescription: msg({
        message:
          'The percentage (between 0 and 100) of employee-days spent commuting on-site by walk. (Typically from commute survey data averaged by office location.)',
        context: 'Percent commute by walk',
      }),
    },
    employee_full_time_equivalent: {
      displayName: msg({
        message: 'Full-time equivalent',
        context:
          'The relative hours worked compared to a typical full-time role. An FTE of 1.0 equates to full-time, while 0.5 reflects half the typical hours.',
      }),
      externalDescription: msg({
        message:
          'The relative hours worked compared to a typical full-time role. An FTE of 1.0 equates to full-time, while 0.5 reflects half the typical hours.',
        context: 'Full-time equivalent',
      }),
    },
    employee_quantity: {
      displayName: msg({
        message: 'Employee-months',
        context:
          'The product of an employee and the number of months the employee worked.',
      }),
      externalDescription: msg({
        message:
          'The product of an employee and the number of months the employee worked.',
        context: 'Employee-months',
      }),
    },
    employee_remote_time_percent: {
      displayName: msg({
        message: 'Remote percentage',
        context: 'The percent of time an employee works remotely.',
      }),
      externalDescription: msg({
        message: 'The percent of time an employee works remotely.',
        context: 'Remote percentage',
      }),
    },
  },
} as const satisfies BatMetadataConfigEntry;
